import React, {useEffect, useMemo, useState, useCallback, useRef} from 'react'
import {Modal} from 'react-bootstrap'
import {Accessibility} from 'lucide-react'
import './IncomingCallModal.css'
import {useMarkNotificationReceivedMutation} from '../app/redux/api'
import { useAWSConnect } from '../app/context/ConnectStreamsContext'
import { callLogger } from '../Utils/ccpLogger'
import {
  CALL_A11Y,
  buildIncomingCallAnnouncement,
  isAcceptCallShortcut,
  isDeclineCallShortcut,
  requestCcpWidgetFocus,
} from './ConnectStreamsWidget/callAccessibility'
import { isMicrophoneUnavailable } from '../services/softphoneRecovery'

interface CallData {
  callId: string
  action: string
  caller: string
  language: string
  channel: string
  type: string
  timestamp: string
  callHeaderId: string
}

interface IncomingCallModalProps {
  show: boolean
  callData: CallData | null
  /**
   * True once this interpreter has accepted and the outcome is not yet known.
   * The modal keeps its exact layout and only swaps the title; the buttons stay put
   * in the spinner/disabled state they already use while an accept is in flight.
   */
  isStandby?: boolean
  onAccept: (callId: string) => void
  onReject: (callId: string, isAutoRejected?: boolean) => void
  onClose: () => void
}

export const IncomingCallModal: React.FC<IncomingCallModalProps> = ({
  show,
  callData,
  isStandby = false,
  onAccept,
  onReject,
  onClose,
}) => {
  const [isRinging, setIsRinging] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [markNotificationReceived] = useMarkNotificationReceivedMutation()
  const { isConnected: isCallConnected, isMasterTab, micPermission } = useAWSConnect()
  const isIncomingCallMaster = isMasterTab === true
  const isMicBlocked = isMicrophoneUnavailable(micPermission)
  const handleRejectRef = useRef<(isAutoRejected?: boolean) => Promise<void>>()
  const hasCalledAvailableAgentsRef = useRef<string | null>(null) // Track which callHeaderId we've called for
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const prevShowRef = useRef<boolean>(false)
  const lastShownCallRef = useRef<CallData | null>(null)
  const didUserInteractRef = useRef<boolean>(false)
  const didSuppressBecauseConnectedRef = useRef<boolean>(false)
  const didSuppressBecauseMicBlockedRef = useRef<boolean>(false)
  const loggedNotificationForCallKeyRef = useRef<string | null>(null)
  const acceptButtonRef = useRef<HTMLButtonElement | null>(null)
  const [a11yAnnouncement, setA11yAnnouncement] = useState('')

  // The buttons already render a spinner + disabled state via isLoading. Standby simply
  // holds that state, which is why the modal looks unchanged apart from the title.
  const isBusy = isLoading || isStandby

  const handleReject = useCallback(
    async (isAutoRejected: boolean = false) => {
      if (callData && !isLoading) {
        setIsLoading(true)
        didUserInteractRef.current = true
        callLogger.info(isAutoRejected ? '🔷 Incoming call auto-rejected' : '🔷 Incoming call rejected (UI)', {
          callId: callData.callId,
          callHeaderId: callData.callHeaderId,
          language: callData.language,
          channel: callData.channel,
          type: callData.type,
          timestamp: callData.timestamp,
        })

        // Stop ringing immediately when call is rejected
        setIsRinging(false)

        // Stop audio immediately
        if (audioRef.current) {
          audioRef.current.pause()
          audioRef.current.currentTime = 0
        }

        try {
          // Call hook's reject function - it will handle API call and close modal after response
          await onReject(callData.callId, isAutoRejected)
        } finally {
          setIsLoading(false)
        }
      }
    },
    [callData, onReject, isLoading]
  )

  // Keep ref updated with latest handleReject
  useEffect(() => {
    handleRejectRef.current = handleReject
  }, [handleReject])

  const handleAccept = useCallback(async () => {
    if (callData && !isLoading) {
      setIsLoading(true)
      didUserInteractRef.current = true
      callLogger.info('🔷 Incoming call accepted (UI)', {
        callId: callData.callId,
        callHeaderId: callData.callHeaderId,
        language: callData.language,
        channel: callData.channel,
        type: callData.type,
        timestamp: callData.timestamp,
        wasAlreadyConnected: Boolean(isCallConnected),
      })

      // Stop ringing when call is accepted
      setIsRinging(false)

      // Stop audio immediately
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }

      try {
        requestCcpWidgetFocus()
        // Call hook's accept function — handles backend notification + queue state.
        // On-demand CCP contacts auto-accept in streamsStore.onConnecting; scheduled
        // calls are accepted manually in the CCP ringer UI when the contact arrives.
        // The modal is closed by useIncomingCall when the store emits contact:connected.
        await onAccept(callData.callId)
      } catch (error) {
        console.error('❌ Error accepting call:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }, [callData, isLoading, isCallConnected, onAccept])

  // Call mark-notification-received API when modal opens
  useEffect(() => {
    if (show && callData) {
      const callKey = `${callData.callId}:${callData.callHeaderId}`
      if (loggedNotificationForCallKeyRef.current !== callKey) {
        loggedNotificationForCallKeyRef.current = callKey
        didUserInteractRef.current = false
        didSuppressBecauseConnectedRef.current = false
        didSuppressBecauseMicBlockedRef.current = false
        lastShownCallRef.current = callData
        callLogger.info('🔷 Incoming call notification received', {
          callId: callData.callId,
          callHeaderId: callData.callHeaderId,
          language: callData.language,
          channel: callData.channel,
          type: callData.type,
          timestamp: callData.timestamp,
        })
      }

      // Try multiple possible field names for callHeaderId
      const callHeaderId =
        callData.callHeaderId ||
        (callData as any).call_header_id ||
        (callData as any).headerId ||
        (callData as any).callHeader ||
        (callData as any)['call-header-id'] ||
        ''

      if (callHeaderId) {
        // Extract numeric part from callHeaderId (e.g., "OPI-003828" -> "003828")
        // Extract all digits including leading zeros
        const numericMatch = String(callHeaderId).match(/\d+/)
        const extractedCallHeaderId = numericMatch ? numericMatch[0] : ''

        // Only call if we haven't called for this specific callHeaderId and we have a valid extraction
        if (
          hasCalledAvailableAgentsRef.current !== String(callHeaderId) &&
          extractedCallHeaderId.length > 0
        ) {
          hasCalledAvailableAgentsRef.current = String(callHeaderId)

          // Send the extracted numeric part (e.g., "003828")
          markNotificationReceived({callHeaderId: extractedCallHeaderId})
            .unwrap()
            .catch((error) => {
              console.error('❌ Error calling mark-notification-received API:', error)
              hasCalledAvailableAgentsRef.current = null // Reset on error so it can retry
              callLogger.warn('🔷 Incoming call mark-notification-received failed', {
                callId: callData.callId,
                callHeaderId: String(callHeaderId),
                extractedCallHeaderId,
                error: String(error),
              })
            })
        }
      }
    }

    // Reset when the modal closes so the next time it opens — including the same
    // call shown again from the queue — logs another appear event. The key guard
    // still suppresses repeats while this showing stays on screen.
    if (!show) {
      hasCalledAvailableAgentsRef.current = null
      loggedNotificationForCallKeyRef.current = null
    }
  }, [show, callData, markNotificationReceived])

  useEffect(() => {
    if (show && isIncomingCallMaster && !isMicBlocked && !isStandby) {
      setIsRinging(true)
      setIsLoading(false) // Reset loading state when modal opens

      // Play notification sound when modal becomes visible
      try {
        if (!audioRef.current) {
          audioRef.current = new Audio('/media/audio/ringtone.mp3')
          audioRef.current.volume = 1.0
        }
        audioRef.current.loop = true
        // Reset and play
        audioRef.current.currentTime = 0
        audioRef.current.play().catch((error) => {
          console.error('❌ Failed to play notification sound:', error)
        })
        console.log('🔔 Playing notification sound for incoming call')
      } catch (error) {
        console.error('❌ Error setting up notification sound:', error)
      }
    } else {
      setIsRinging(false)
      setIsLoading(false) // Reset loading state when modal closes

      // Stop audio when modal closes
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }
    }
  }, [show, isIncomingCallMaster, isMicBlocked, isStandby])

  // Detect "no response" timeout (modal hidden without accept/reject interaction).
  // Timeout is driven by `useIncomingCall` closing the modal after ~20s.
  useEffect(() => {
    const prevShow = prevShowRef.current
    prevShowRef.current = show

    if (prevShow && !show) {
      const lastCall = lastShownCallRef.current
      if (lastCall) {
        callLogger.info('🔷 Incoming call modal disappeared', {
          callId: lastCall.callId,
          callHeaderId: lastCall.callHeaderId,
          language: lastCall.language,
          channel: lastCall.channel,
          type: lastCall.type,
          timestamp: lastCall.timestamp,
        })
        if (
          !didUserInteractRef.current &&
          !didSuppressBecauseConnectedRef.current &&
          !didSuppressBecauseMicBlockedRef.current
        ) {
          callLogger.warn('🔷 Incoming call no response (modal timeout)', {
            callId: lastCall.callId,
            callHeaderId: lastCall.callHeaderId,
            language: lastCall.language,
            channel: lastCall.channel,
            type: lastCall.type,
            timestamp: lastCall.timestamp,
          })
        }
      }
    }
  }, [show])

  // Tab close or navigation while the modal is still up does not flip `show`.
  useEffect(() => {
    return () => {
      if (!prevShowRef.current) return
      const lastCall = lastShownCallRef.current
      if (!lastCall) return
      callLogger.info('🔷 Incoming call modal disappeared', {
        callId: lastCall.callId,
        callHeaderId: lastCall.callHeaderId,
        language: lastCall.language,
        channel: lastCall.channel,
        type: lastCall.type,
        timestamp: lastCall.timestamp,
      })
    }
  }, [])

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  const caller = useMemo<{
    requester: string
    company: string
  }>(() => {
    if (callData?.caller) {
      const caller = JSON.parse(callData.caller)
      return {
        requester: `${caller.firstName || ''} ${caller.lastName || ''}`,
        company: caller.companyName || 'N/A',
      }
    }

    return {
      requester: 'N/A',
      company: 'N/A',
    }
  }, [callData])

  // Announce + focus Accept when modal opens
  useEffect(() => {
    if (!show || !callData || isCallConnected || isMicBlocked || isStandby) {
      setA11yAnnouncement('')
      return
    }
    setA11yAnnouncement(
      buildIncomingCallAnnouncement({
        company: caller.company,
        requester: caller.requester,
      }),
    )
    const id = window.setTimeout(() => {
      acceptButtonRef.current?.focus()
    }, 100)
    return () => window.clearTimeout(id)
  }, [show, callData, isCallConnected, isMicBlocked, isStandby, caller.company, caller.requester])

  // Keyboard shortcuts while modal is visible
  useEffect(() => {
    if (!show || !callData || isCallConnected || isMicBlocked || isBusy) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (isAcceptCallShortcut(event)) {
        event.preventDefault()
        void handleAccept()
        return
      }
      if (isDeclineCallShortcut(event)) {
        event.preventDefault()
        void handleReject(false)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [show, callData, isCallConnected, isMicBlocked, isBusy, handleReject, handleAccept])

  // Failsafe: if a call is already connected, suppress the modal
  useEffect(() => {
    if (show && isCallConnected && callData) {
      console.log('⚠️ IncomingCallModal: Call is already connected, suppressing modal')
      didSuppressBecauseConnectedRef.current = true
      callLogger.breadcrumb('🔷 Incoming call modal suppressed (already connected)', {
        callId: callData.callId,
        callHeaderId: callData.callHeaderId,
        language: callData.language,
        channel: callData.channel,
        type: callData.type,
        timestamp: callData.timestamp,
      })
      // Stop audio immediately
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }
      setIsRinging(false)
      onClose()
    }
  }, [show, isCallConnected, callData, onClose])

  // Failsafe: hide while microphone permission is blocked (same signal as CCP availability restriction).
  useEffect(() => {
    if (show && isMicBlocked && callData) {
      didSuppressBecauseMicBlockedRef.current = true
      callLogger.breadcrumb('🔷 Incoming call modal suppressed (microphone unavailable)', {
        callId: callData.callId,
        callHeaderId: callData.callHeaderId,
        language: callData.language,
        channel: callData.channel,
        type: callData.type,
        timestamp: callData.timestamp,
        micPermission,
      })
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }
      setIsRinging(false)
      onClose()
    }
  }, [show, isMicBlocked, callData, micPermission, onClose])

  if (!callData || isCallConnected || isMicBlocked || !isIncomingCallMaster) return null

  return (
    <Modal
      show={show}
      onHide={handleReject}
      centered
      backdrop='static'
      keyboard={false}
      className='incoming-call-modal'
      dialogClassName='incoming-call-dialog'
      aria-labelledby='incoming-call-modal-title'
    >
      <Modal.Body className='text-center p-8'>
        <div
          className='visually-hidden'
          role='status'
          aria-live='assertive'
          aria-atomic='true'
        >
          {a11yAnnouncement}
        </div>
        {/* Call Information */}
        <div className='call-info'>
          <h4 id='incoming-call-modal-title' className='text-dark mb-4'>
            {isStandby ? (
              <span className='incoming-call-standby'>Please Standby</span>
            ) : (
              'Incoming Call'
            )}
          </h4>
          {isStandby && (
            <p className='text-muted fs-6 mb-4 px-2'>
              Connecting your call. Keep this window open until you are connected.
            </p>
          )}

          {/* Avatar and Company Name */}
          <div className='mb-4 d-flex flex-column align-items-center justify-content-center'>
            <div className='avatar'>
              <div className={`calling-animation mb-4 ${isRinging ? 'ringing' : ''}`}>
                <div className='avatar-icon'>
                  <span className='avatar-icon-text'>
                    {(caller.company ?? '').charAt(0).toUpperCase() +
                      (caller.company ?? '').charAt(1).toUpperCase()}
                  </span>
                </div>
                <div className='pulse-rings'>
                  <div className='pulse-ring pulse-ring-1'></div>
                  <div className='pulse-ring pulse-ring-2'></div>
                  <div className='pulse-ring pulse-ring-3'></div>
                </div>
              </div>
            </div>

            <h4 className='text-primary fw-bold fs-1'>{caller.company}</h4>
            <h5 className='text-muted mb-4 small'>{caller.requester}</h5>
          </div>

          <div className='call-details'>
            <div className='row text-center'>
              <div className='col-4'>
                <div className='detail-item'>
                  <div className='icon-container'>
                    <i className='bi bi-translate text-muted mb-1'></i>
                  </div>
                  <div className='detail-item-content'>
                    <div className='small text-muted'>Language</div>
                    <div className='fw-bold'>{callData.language}</div>
                  </div>
                </div>
              </div>
              <div className='col-4'>
                <div className='detail-item'>
                  <div className='icon-container'>
                    <i className='bi bi-broadcast text-muted mb-1'></i>
                  </div>
                  <div className='detail-item-content'>
                    <div className='small text-muted'>Channel</div>
                    <div className='fw-bold'>{callData.channel}</div>
                  </div>
                </div>
              </div>
              <div className='col-4'>
                <div className='detail-item'>
                  <div className='icon-container'>
                    <i className='bi bi-clock text-muted mb-1'></i>
                  </div>
                  <div className='detail-item-content'>
                    <div className='small text-muted'>Type</div>
                    <div className='fw-bold'>{callData.type.replace('_', ' ')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className='incoming-call-a11y'
          aria-labelledby='incoming-call-a11y-title'
        >
          <div className='incoming-call-a11y-title' id='incoming-call-a11y-title'>
            <Accessibility size={11} aria-hidden='true' />
            Accessibility
          </div>
          <div className='incoming-call-a11y-shortcuts'>
            <span>
              Accept: <kbd>{CALL_A11Y.acceptHint}</kbd>
            </span>
            <span>
              Decline: <kbd>{CALL_A11Y.declineHint}</kbd>
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className='call-actions'>
          <div className='action-btn-container'>
            <button
              type='button'
              className='btn btn-danger btn-xl rounded-circle action-btn reject-btn'
              onClick={() => handleReject(false)}
              disabled={isBusy}
              aria-label='Decline call'
              aria-keyshortcuts={CALL_A11Y.declineShortcut}
              style={{
                width: '70px',
                height: '70px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                transition: 'all 0.3s ease',
                opacity: isBusy ? 0.6 : 1,
                cursor: isBusy ? 'not-allowed' : 'pointer',
              }}
            >
              {isBusy ? (
                <span className='spinner-border spinner-border-sm text-light' role='status' aria-hidden='true'></span>
              ) : (
                <i className='bi bi-telephone-x-fill action-btn-icon' aria-hidden='true'></i>
              )}
            </button>
            <label className='text-danger'>Reject</label>
          </div>
          <div className='action-btn-container'>
            <button
              ref={acceptButtonRef}
              type='button'
              className='btn btn-success btn-xl rounded-circle action-btn accept-btn'
              onClick={handleAccept}
              disabled={isBusy}
              aria-label='Accept call'
              aria-keyshortcuts={CALL_A11Y.acceptShortcut}
              style={{
                width: '70px',
                height: '70px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                transition: 'all 0.3s ease',
                opacity: isBusy ? 0.6 : 1,
                cursor: isBusy ? 'not-allowed' : 'pointer',
              }}
            >
              {isBusy ? (
                <span className='spinner-border spinner-border-sm text-light' role='status' aria-hidden='true'></span>
              ) : (
                <i className='bi bi-telephone-fill action-btn-icon' aria-hidden='true'></i>
              )}
            </button>
            <label className='text-success'>Accept</label>
          </div>
        </div>

        {/* Call ID for debugging */}
        <div className='mt-3'>
          <small className='text-muted'>Call ID: {callData.callHeaderId}</small>
        </div>
      </Modal.Body>
    </Modal>
  )
}
